import styled from '@emotion/styled'
import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import Breakpoint from '../../../Breakpoint'
import Loader from '../../../components/global/Loader'
import SearchInput from '../../../components/global/SearchInput'
import SkillCard from '../../../components/global/SkillCard'
import Title from '../../../components/global/Title'
import { SERVER_URL, apiHeader } from '../../../config'
import UserContext from '../../../context/UserContext'
import { useVerifyCoachToken } from '../../../hooks/useVerifyCoachToken'
import { Skill } from '../../../types/skill.type'
import { StyledButton } from '../../admin/Login'
import { SkillsWrapper } from '../my-members/CoachMyMembers'
import CoachSkillFormModal from './CoachSkillFormModal'

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 20px;

    & > div {
        flex: 1;
        width: 100%;
    }

    @media (max-width: ${Breakpoint.TABLET_L}) {
        flex-direction: column;
        gap: 0;
    }
`

export default function CoachSkills() {
    const user = useContext(UserContext)
    const { t } = useTranslation()
    const verifyToken = useVerifyCoachToken()

    const [skillToEdit, setSkillToEdit] = useState<Skill>()
    const [showEditSkillModal, setShowEditSkillModal] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    const { data: skills, refetch: refetchSkills } = useQuery({
        queryKey: ['skills'],
        queryFn: async () => {
            verifyToken()
            const response = await axios.get<Skill[]>(`${SERVER_URL}/skills`, apiHeader(user.tokenCoach))
            return response.data
        },
    })

    const { mutate: deleteSkill } = useMutation({
        mutationFn: async (id: number) => {
            verifyToken()
            await axios.delete(`${SERVER_URL}/skills/${id}`, apiHeader(user.tokenCoach))
            refetchSkills()
            toast.success(t('coach.skill-deleted'))
        },
    })

    const filteredSkills = useMemo(() => {
        if (!skills) return []
        return skills.filter((skill) => skill.name.toLowerCase().includes(searchTerm.toLowerCase()))
    }, [skills, searchTerm])

    if (!skills) return <Loader />

    const editSkill = (skill: Skill) => {
        setSkillToEdit(skill)
        setShowEditSkillModal(true)
    }

    return (
        <div>
            <TitleWrapper>
                <div>
                    <Title>{t('coach.skills')}</Title>
                    <SearchInput
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Rechercher une compétence..."
                    />
                </div>
                <StyledButton
                    className="btn btn-primary"
                    onClick={() => {
                        setShowEditSkillModal(true)
                    }}
                >
                    Créer une compétence
                </StyledButton>
            </TitleWrapper>
            <SkillsWrapper>
                {filteredSkills.length === 0 ? (
                    <p>Aucune compétence trouvée</p>
                ) : (
                    filteredSkills.map((skill) => (
                        <SkillCard
                            key={skill.id}
                            name={skill.name}
                            deleteFn={() => deleteSkill(skill.id)}
                            editFn={() => editSkill(skill)}
                            parentSkill={skill.parentSkill}
                        />
                    ))
                )}
            </SkillsWrapper>
            {showEditSkillModal && (
                <CoachSkillFormModal
                    refetchCoachSkills={refetchSkills}
                    setShowEditSkillModal={setShowEditSkillModal}
                    skillToEdit={skillToEdit}
                    setSkillToEdit={setSkillToEdit}
                />
            )}
        </div>
    )
}
